// --- React ---
import React from 'react';

// --- 공통 UI 컴포넌트 ---
import { Text, Flex } from '@/components/ui';

// --- 타입 ---
import type { MonitoringDataItem } from './monitoringDeviceTypes';

// --- 유틸 ---
import {
  sensorSupportsTwaStel,
  sensorSupportsCustomLel,
  sensorSupportsVolFor50Lel,
  formatDateTime,
} from './monitoringDeviceUtils';

// --- 스타일 ---
import {
  ExpandedContent,
  SensorDataTable,
  DataBox,
  DataBoxHeader,
  TableHead,
  TableHeaderCell,
  DataBoxRow,
  DataBoxLabel,
  DataBoxValue,
} from './MonitoringDeviceList.styles';

// ==================== 타입 정의 ====================

interface DeviceExpandedDetailProps {
  item: MonitoringDataItem;
}

// ==================== 컴포넌트 ====================

/**
 * 디바이스 확장 상세 정보 패널.
 * Monitoring / Information / Configuration 세 섹션을 표시.
 */
export const DeviceExpandedDetail: React.FC<DeviceExpandedDetailProps> = ({ item }) => {
  // Sensor Disabled 상태가 아닌 활성 센서만 필터링
  const activeSensorEntries = Object.entries(item.monitoring.sensors || {}).filter(
    ([, sensor]: [string, any]) => {
      const statusNames = Array.isArray(sensor.status_name) ? sensor.status_name : [];
      return !statusNames.includes('Sensor Disabled');
    }
  );
  const activeSensorNames = new Set(activeSensorEntries.map(([name]) => name));

  // Information 센서 인덱스 필터링 (NO_SENSOR, sensor_disabled, monitoring disabled 제외)
  const sensorNames: string[] = item.information?.sensor_name || [];
  const sensorTypes: number[] = item.information?.sensor_type || [];
  const activeSensorIndices = sensorNames
    .map((_: string, idx: number) => idx)
    .filter((idx: number) => {
      // NO_SENSOR (255) 제외
      if (sensorTypes[idx] === 255) return false;
      // configuration의 sensor_disabled 체크
      const configDisabled = item.configuration?.sensor_disabled?.[idx];
      if (configDisabled) return false;
      // monitoring sensors에서 disabled 체크
      const name = sensorNames[idx];
      if (name && activeSensorNames.size > 0 && !activeSensorNames.has(name)) return false;
      return true;
    });

  // ==================== 렌더링 ====================

  return (
    <ExpandedContent data-tour="monitoring-device-detail">
      <Flex
        direction={item.device_type === 'bellmini' ? 'row' : 'column'}
        gap={10}
        align="start"
      >
        <Flex direction="column" gap={10}>
          {/* ── 모니터링 섹션 ── */}
          <Flex direction="column" align="start" justify="start" gap={2}>
            <Text size="lg" weight="semibold">
              Monitoring
            </Text>
            <Flex align="stretch" justify="start" gap={2}>
              {/* 일반 정보 (GPS 좌표) */}
              <DataBox deviceType={item.device_type}>
                <DataBoxHeader>General</DataBoxHeader>
                <DataBoxRow>
                  <DataBoxLabel>latitude</DataBoxLabel>
                  <DataBoxValue>{item.monitoring.latitude}</DataBoxValue>
                </DataBoxRow>
                <DataBoxRow>
                  <DataBoxLabel>longitude</DataBoxLabel>
                  <DataBoxValue>{item.monitoring.longitude}</DataBoxValue>
                </DataBoxRow>
              </DataBox>

              {/* 센서별 모니터링 데이터 (Disabled 센서 제외) */}
              <SensorDataTable>
                <TableHead>
                  <tr>
                    <TableHeaderCell></TableHeaderCell>
                    {activeSensorEntries.map(([name]) => (
                      <TableHeaderCell key={name}>{name}</TableHeaderCell>
                    ))}
                  </tr>
                </TableHead>
                <tbody>
                  <tr>
                    <td>Sensor Type</td>
                    {activeSensorEntries.map(([, sensor]: [string, any], idx) => (
                      <td key={idx}>{sensor.sensor_type}</td>
                    ))}
                  </tr>
                  <tr>
                    <td>Status Name</td>
                    {activeSensorEntries.map(([, sensor]: [string, any], idx) => (
                      <td key={idx}>{sensor.status_name?.join(', ')}</td>
                    ))}
                  </tr>
                  <tr>
                    <td>Gas Conc</td>
                    {activeSensorEntries.map(([, sensor]: [string, any], idx) => (
                      <td key={idx}>
                        {sensor.gas_conc !== null && sensor.gas_conc !== undefined
                          ? `${sensor.gas_conc} ${sensor.conc_unit}`
                          : '--'}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td>Error</td>
                    {activeSensorEntries.map(([, sensor]: [string, any], idx) => (
                      <td key={idx}>{sensor.error_name}</td>
                    ))}
                  </tr>
                  <tr>
                    <td>Warning</td>
                    {activeSensorEntries.map(([, sensor]: [string, any], idx) => (
                      <td key={idx}>{sensor.warning_name}</td>
                    ))}
                  </tr>
                </tbody>
              </SensorDataTable>
            </Flex>
          </Flex>

          {/* ── 디바이스 정보 섹션 ── */}
          <Flex direction="column" align="start" justify="start" gap={2}>
            <Text size="lg" weight="semibold">
              Information
            </Text>
            <Flex align="stretch" justify="start" gap={2}>
              {/* 일반 정보 (제조일, 버전, 활성화 날짜 등) */}
              <DataBox deviceType={item.device_type}>
                <DataBoxHeader>General</DataBoxHeader>
                <DataBoxRow>
                  <DataBoxLabel>manufacture_date</DataBoxLabel>
                  <DataBoxValue>{item.information?.manufacture_date}</DataBoxValue>
                </DataBoxRow>
                <DataBoxRow>
                  <DataBoxLabel>hw_version</DataBoxLabel>
                  <DataBoxValue>{item.information?.hw_version}</DataBoxValue>
                </DataBoxRow>
                <DataBoxRow>
                  <DataBoxLabel>fw_version</DataBoxLabel>
                  <DataBoxValue>{item.information?.fw_version}</DataBoxValue>
                </DataBoxRow>
                <DataBoxRow>
                  <DataBoxLabel>activation_date_time</DataBoxLabel>
                  <DataBoxValue>{item.information?.activation_date_time}</DataBoxValue>
                </DataBoxRow>
                <DataBoxRow>
                  <DataBoxLabel>min_calibration_due_date</DataBoxLabel>
                  <DataBoxValue>{item.information?.min_calibration_due_date}</DataBoxValue>
                </DataBoxRow>
                <DataBoxRow>
                  <DataBoxLabel>min_bump_test_due_date</DataBoxLabel>
                  <DataBoxValue>{item.information?.min_bump_test_due_date}</DataBoxValue>
                </DataBoxRow>
              </DataBox>

              {/* 센서별 정보 테이블 (Disabled 센서 제외) */}
              <SensorDataTable>
                <TableHead>
                  <tr>
                    <TableHeaderCell></TableHeaderCell>
                    {activeSensorIndices.map((idx: number) => (
                      <TableHeaderCell key={idx}>{sensorNames[idx]}</TableHeaderCell>
                    ))}
                  </tr>
                </TableHead>
                <tbody>
                  <tr>
                    <td>Calibration Due Date</td>
                    {activeSensorIndices.map((idx: number) => (
                      <td key={idx}>{(item.information?.calibration_due_date || [])[idx]}</td>
                    ))}
                  </tr>
                  <tr>
                    <td>Bump Due Date</td>
                    {activeSensorIndices.map((idx: number) => (
                      <td key={idx}>{(item.information?.bump_due_date || [])[idx]}</td>
                    ))}
                  </tr>
                  <tr>
                    <td>Last Calibration Days</td>
                    {activeSensorIndices.map((idx: number) => (
                      <td key={idx}>{(item.information?.last_calibration_days || [])[idx]}</td>
                    ))}
                  </tr>
                  <tr>
                    <td>Last Bump Days</td>
                    {activeSensorIndices.map((idx: number) => (
                      <td key={idx}>{(item.information?.last_bump_days || [])[idx]}</td>
                    ))}
                  </tr>
                  <tr>
                    <td>Unit</td>
                    {activeSensorIndices.map((idx: number) => (
                      <td key={idx}>{(item.information?.unit || [])[idx]}</td>
                    ))}
                  </tr>
                  <tr>
                    <td>Decimal Point</td>
                    {activeSensorIndices.map((idx: number) => (
                      <td key={idx}>{(item.information?.decimal_point || [])[idx]}</td>
                    ))}
                  </tr>
                </tbody>
              </SensorDataTable>
            </Flex>
          </Flex>
        </Flex>

        {/* ── 설정 섹션 ── */}
        <Flex direction="column" align="start" justify="start" gap={2}>
          <Text size="lg" weight="semibold">
            Configuration
          </Text>
          <Flex align="stretch" justify="start" gap={2}>
            {/* 일반 설정 */}
            <DataBox deviceType={item.device_type}>
              <DataBoxHeader>General</DataBoxHeader>
              <DataBoxRow>
                <DataBoxLabel>stealth_mode</DataBoxLabel>
                <DataBoxValue>{String(item.configuration?.stealth_mode)}</DataBoxValue>
              </DataBoxRow>
              <DataBoxRow>
                <DataBoxLabel>safe_mode</DataBoxLabel>
                <DataBoxValue>{String(item.configuration?.safe_mode)}</DataBoxValue>
              </DataBoxRow>
              <DataBoxRow>
                <DataBoxLabel>latching_alarm</DataBoxLabel>
                <DataBoxValue>{String(item.configuration?.latching_alarm)}</DataBoxValue>
              </DataBoxRow>
              <DataBoxRow>
                <DataBoxLabel>low_alarm_ack</DataBoxLabel>
                <DataBoxValue>{String(item.configuration?.low_alarm_ack)}</DataBoxValue>
              </DataBoxRow>
              <DataBoxRow>
                <DataBoxLabel>confidence_beep</DataBoxLabel>
                <DataBoxValue>{String(item.configuration?.confidence_beep)}</DataBoxValue>
              </DataBoxRow>
              {item.device_type !== 'bellmini' && (
                <DataBoxRow>
                  <DataBoxLabel>green_light</DataBoxLabel>
                  <DataBoxValue>{String(item.configuration?.green_light)}</DataBoxValue>
                </DataBoxRow>
              )}
              <DataBoxRow>
                <DataBoxLabel>confidence_interval</DataBoxLabel>
                <DataBoxValue>{item.configuration?.confidence_interval}</DataBoxValue>
              </DataBoxRow>
              <DataBoxRow>
                <DataBoxLabel>intelliflash_interval</DataBoxLabel>
                <DataBoxValue>{item.configuration?.intelliflash_interval}</DataBoxValue>
              </DataBoxRow>
              <DataBoxRow>
                <DataBoxLabel>datalog_interval</DataBoxLabel>
                <DataBoxValue>{item.configuration?.datalog_interval}</DataBoxValue>
              </DataBoxRow>
              <DataBoxRow>
                <DataBoxLabel>worker_name</DataBoxLabel>
                <DataBoxValue>{item.configuration?.worker_name}</DataBoxValue>
              </DataBoxRow>
              <DataBoxRow>
                <DataBoxLabel>location_name</DataBoxLabel>
                <DataBoxValue>{item.configuration?.location_name}</DataBoxValue>
              </DataBoxRow>
              <DataBoxRow>
                <DataBoxLabel>timestamp</DataBoxLabel>
                <DataBoxValue>
                  {item.configuration?.timestamp
                    ? formatDateTime(item.configuration.timestamp as string)
                    : '-'}
                </DataBoxValue>
              </DataBoxRow>
              <DataBoxRow>
                <DataBoxLabel>set_date_time</DataBoxLabel>
                <DataBoxValue>{String(item.configuration?.set_date_time)}</DataBoxValue>
              </DataBoxRow>
              {item.device_type !== 'bellmini' && (
                <DataBoxRow>
                  <DataBoxLabel>span_cal_and_bump</DataBoxLabel>
                  <DataBoxValue>{String(item.configuration?.span_cal_and_bump)}</DataBoxValue>
                </DataBoxRow>
              )}
              {item.device_type !== 'bellmini' && (
                <DataBoxRow>
                  <DataBoxLabel>startup_message</DataBoxLabel>
                  <DataBoxValue>{item.configuration?.startup_message}</DataBoxValue>
                </DataBoxRow>
              )}
            </DataBox>

            {/* 센서별 설정 테이블 (Disabled/NO_SENSOR 제외) */}
            <SensorDataTable>
              <TableHead>
                <tr>
                  <TableHeaderCell></TableHeaderCell>
                  {activeSensorIndices.map((idx: number) => (
                    <TableHeaderCell key={idx}>{sensorNames[idx]}</TableHeaderCell>
                  ))}
                </tr>
              </TableHead>
              <tbody>
                <tr>
                  <td>Calibration Gas Conc</td>
                  {activeSensorIndices.map((idx: number) => (
                    <td key={idx}>{(item.configuration?.calibration_gas_conc || [])[idx]}</td>
                  ))}
                </tr>
                <tr>
                  <td>Calibration Interval</td>
                  {activeSensorIndices.map((idx: number) => (
                    <td key={idx}>{(item.configuration?.calibration_interval || [])[idx]}</td>
                  ))}
                </tr>
                <tr>
                  <td>Bump Interval</td>
                  {activeSensorIndices.map((idx: number) => (
                    <td key={idx}>{(item.configuration?.bump_interval || [])[idx]}</td>
                  ))}
                </tr>
                <tr>
                  <td>Low Alarm</td>
                  {activeSensorIndices.map((idx: number) => (
                    <td key={idx}>{(item.configuration?.low_alarm || [])[idx]}</td>
                  ))}
                </tr>
                <tr>
                  <td>High Alarm</td>
                  {activeSensorIndices.map((idx: number) => (
                    <td key={idx}>{(item.configuration?.high_alarm || [])[idx]}</td>
                  ))}
                </tr>
                <tr>
                  <td>TWA Alarm</td>
                  {activeSensorIndices.map((idx: number) => {
                    const st = (item.configuration?.sensor_type || [])[idx];
                    const supports = sensorSupportsTwaStel(st);
                    if (item.device_type === 'bellmini' && !supports) return null;
                    return <td key={idx}>{supports ? (item.configuration?.twa_alarm || [])[idx] : '-'}</td>;
                  })}
                </tr>
                <tr>
                  <td>STEL Alarm</td>
                  {activeSensorIndices.map((idx: number) => {
                    const st = (item.configuration?.sensor_type || [])[idx];
                    const supports = sensorSupportsTwaStel(st);
                    if (item.device_type === 'bellmini' && !supports) return null;
                    return <td key={idx}>{supports ? (item.configuration?.stel_alarm || [])[idx] : '-'}</td>;
                  })}
                </tr>
                <tr>
                  <td>STEL Period</td>
                  {activeSensorIndices.map((idx: number) => {
                    const st = (item.configuration?.sensor_type || [])[idx];
                    const supports = sensorSupportsTwaStel(st);
                    if (item.device_type === 'bellmini' && !supports) return null;
                    return <td key={idx}>{supports ? (item.configuration?.stel_period || [])[idx] : '-'}</td>;
                  })}
                </tr>
                <tr>
                  <td>TWA Period</td>
                  {activeSensorIndices.map((idx: number) => {
                    const st = (item.configuration?.sensor_type || [])[idx];
                    const supports = sensorSupportsTwaStel(st);
                    if (item.device_type === 'bellmini' && !supports) return null;
                    return <td key={idx}>{supports ? (item.configuration?.twa_period || [])[idx] : '-'}</td>;
                  })}
                </tr>
                <tr>
                  <td>Auto Fresh Air Cal</td>
                  {activeSensorIndices.map((idx: number) => (
                    <td key={idx}>{String((item.configuration?.auto_fresh_air_cal || [])[idx])}</td>
                  ))}
                </tr>
                {item.device_type !== 'bellmini' && (
                  <tr>
                    <td>Custom LEL</td>
                    {activeSensorIndices.map((idx: number) => {
                      const st = (item.configuration?.sensor_type || [])[idx];
                      const supports = sensorSupportsCustomLel(st);
                      return <td key={idx}>{supports ? (item.configuration?.custom_lel || [])[idx] : '-'}</td>;
                    })}
                  </tr>
                )}
                {item.device_type !== 'bellmini' && (
                  <tr>
                    <td>Vol for 50 LEL CH4</td>
                    {activeSensorIndices.map((idx: number) => {
                      const st = (item.configuration?.sensor_type || [])[idx];
                      const supports = sensorSupportsVolFor50Lel(st);
                      return <td key={idx}>{supports ? (item.configuration?.vol_for_50_lel_ch4 || [])[idx] : '-'}</td>;
                    })}
                  </tr>
                )}
                {item.device_type !== 'bellmini' && (
                  <tr>
                    <td>Bump Test Threshold</td>
                    {activeSensorIndices.map((idx: number) => (
                      <td key={idx}>{(item.configuration?.bump_test_threshold || [])[idx]}</td>
                    ))}
                  </tr>
                )}
              </tbody>
            </SensorDataTable>
          </Flex>
        </Flex>
      </Flex>
    </ExpandedContent>
  );
};
